import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  CardGroup,
  CardGroupSwitchCardProps,
  Icon,
  LinkCard,
  Page,
} from "@inductiveautomation/ws-components";

import { EmailNewsletterId } from "../../../components/EmailPreferencesForm/EmailPreferencesForm.types";
import { idForKey } from "./utilities";
import { getHubspotEmailPreferencesStyles } from "./_hubspotEmailPreferences.styles";

const WORKSPACE_ACTIVITY_ID = idForKey.workspacesActivity;

export interface HubspotEmailPreferencesProps {
  primaryEmailAddress: string;
  // Vendor (Hubspot) hosted preference center, from the communication endpoint.
  // Absent until the BE ships TS-2761, so the Manage button hides when missing.
  vendorEmailPreferencesManagementUrl?: string;
  // tsAccountWorkspaceVisibilityFe. The Workspace activity toggle is the only
  // toggle on this page; when off (current prod) the Manage button is the only
  // actionable control, matching the ticket.
  showWorkspaceActivity: boolean;
  selectedIds: string[];
  idToDisable?: EmailNewsletterId | "unsubscribe_all" | "initial_load";
  isLoading: boolean;
  onChange: (args: { id: EmailNewsletterId; value: boolean }) => void;
}

const HubspotEmailPreferences = ({
  primaryEmailAddress,
  vendorEmailPreferencesManagementUrl,
  showWorkspaceActivity,
  selectedIds,
  idToDisable,
  isLoading,
  onChange,
}: HubspotEmailPreferencesProps) => {
  const { t } = useTranslation();
  const { classes } = getHubspotEmailPreferencesStyles();

  const isVendorUnavailable = !vendorEmailPreferencesManagementUrl;

  const workspaceActivityCard: CardGroupSwitchCardProps = {
    id: WORKSPACE_ACTIVITY_ID,
    inputType: "switch",
    label: t("email_preferences.cards.workspace_activity.label"),
    description: t("email_preferences.cards.workspace_activity.description"),
    value: "",
    checked: selectedIds.includes(WORKSPACE_ACTIVITY_ID),
    onValueChange: (id, value) =>
      onChange({ id: id as EmailNewsletterId, value }),
    disabled:
      idToDisable === WORKSPACE_ACTIVITY_ID ||
      idToDisable === "unsubscribe_all",
  };

  return (
    <Page
      layout="content_page"
      isLoading={isLoading}
      sections={[
        ...(showWorkspaceActivity
          ? [
              {
                heading: t(
                  "email_preferences.hubspot.account_system.heading"
                ),
                headingLevel: "h1" as const,
                loadingVariant: "card_group" as const,
                fieldCount: 1,
                description: (
                  <Trans i18nKey="email_preferences.hubspot.account_system.description">
                    {{ email: primaryEmailAddress }}
                  </Trans>
                ),
                children: <CardGroup cards={[workspaceActivityCard]} />,
              },
            ]
          : []),
        {
          heading: t("email_preferences.hubspot.marketing.heading"),
          loadingVariant: "card_group",
          fieldCount: 1,
          description: t("email_preferences.hubspot.marketing.description"),
          children: (
            <div className={classes.marketingSection}>
              {isVendorUnavailable ? (
                <Alert
                  id="email-preferences-vendor-unavailable"
                  testId="email-preferences-vendor-unavailable"
                  variant="error"
                  title={t(
                    "email_preferences.hubspot.marketing.unavailable_error_title"
                  )}
                  description={t(
                    "email_preferences.hubspot.marketing.unavailable_error"
                  )}
                  closeButtonEventHandler={() => undefined}
                />
              ) : null}

              <LinkCard
                label={t("email_preferences.hubspot.marketing.row_label")}
                buttonLabel={t("email_preferences.hubspot.manage_button")}
                href={vendorEmailPreferencesManagementUrl}
                endIcon={<Icon name="open" />}
                testId="email-preferences-manage-vendor"
              />
            </div>
          ),
        },
      ]}
    />
  );
};

export default HubspotEmailPreferences;
